import { useStores } from 'hooks/useStores';
import { useState, useEffect } from 'react';
import {
  useKpiIndicatorListLazyQuery,
  useKpiIndicatorAddMutation,
  useKpiIndicatorDeleteMutation,
  useKpiIndicatorUpdateMutation,
} from 'generated/graphql';
import { KPI } from 'types/kpi';
import { IBaseResponse } from 'types/common/baseResponse';
import { toJS } from 'mobx';

export const KPI_TYPES = {
  CURRENCY_USD: 'Currency ($)',
  CURRENCY_GBP: 'Currency (GBP)',
  CURRENCY_EUR: 'Currency (Euro)',
  TEXT: 'Text',
  PERCENT: '%',
  NUMBER: 'Number (Whole)',
  FRACTION: 'Fraction',
};

const typeOptions = Object.values(KPI_TYPES);

const ACCOUNT_ID = 'account id';

const isKPIIncluded = (kpi: any, { searchValue, groupId }: any) => {
  const searchValueLowered = searchValue.toLowerCase();
  const matchesGroup = groupId === 'All Groups' || groupId === kpi.group;
  return (
    (kpi.name.toLowerCase().includes(searchValueLowered) ||
      kpi.group.toLowerCase().includes(searchValueLowered) ||
      kpi.type.toLowerCase().includes(searchValueLowered) ||
      kpi.description.toLowerCase().includes(searchValueLowered)) &&
    matchesGroup
  );
};
const getNewKPI = () => ({
  id: new Date().getTime(),
  name: '',
  type: '',
  group: '',
  description: '',
});
export function useLibrary() {
  const { KPIStore } = useStores();
  const [searchValue, setSearchValue] = useState('');
  const [editKpiId, setEditKpiId] = useState();
  const [getKpis, { loading, data }] = useKpiIndicatorListLazyQuery();
  const [addKpi] = useKpiIndicatorAddMutation();
  const [deleteKpi] = useKpiIndicatorDeleteMutation();
  const [updateKpi] = useKpiIndicatorUpdateMutation();

  const [groupId, setGroupId] = useState('All Groups');
  const [openKPIDrawer, setOpenKPIDrawer] = useState(false);
  const [types, setTypes] = useState<string[]>([]);

  function loadGroupedKPIs(kpis = KPIStore.kpis) {
    const result = {} as any;
    const kpisByIds = {} as any;
    for (let i = 0; i < kpis.length; i++) {
      let kpi = kpis[i];
      if (!result[kpi.group]) result[kpi.group] = [];
      result[kpi.group].push(kpi);
      kpisByIds[kpi.id] = kpi;
    }

    const groupedKPIs = Object.entries(result).map(([group, kpis]: any) => ({
      name: group,
      kpis: kpis.map((item: any) => ({ ...item, name: item.name })),
    }));

    return { groupedKPIs, kpisByIds };
  }

  useEffect(() => {
    if (data && data.kpiIndicatorList) {
      // @ts-ignore
      KPIStore.kpis = data.kpiIndicatorList;
      // @ts-ignore
      const { groupedKPIs, kpisByIds } = loadGroupedKPIs(data.kpiIndicatorList);
      KPIStore.groupedKPIs = groupedKPIs;
      KPIStore.kpisByIds = kpisByIds;
    }
  }, [data?.kpiIndicatorList]);

  return {
    operations: {
      handleSearch: (e: any) => {
        setSearchValue(e.target.value);
      },
      handleGroupsChange: (e: any) => {
        setGroupId(e.target.value);
      },
      openEditKPIDrawer: (row: any) => {
        setEditKpiId(row.original.id);
        KPIStore.editorKPIs = [{ ...row.original }];
        setOpenKPIDrawer(true);
      },
      openNewKPIDrawer: () => {
        KPIStore.editorKPIs = [getNewKPI()];
        setOpenKPIDrawer(true);
      },
      closeKPIDrawer: () => {
        KPIStore.editorKPIs = [getNewKPI()];
        setOpenKPIDrawer(false);
      },
      loadGroupedKPIs: (kpis: any) => {
        return loadGroupedKPIs(kpis);
      },
      loadTypes: () => {
        setTypes(typeOptions);
      },
      loadKPIs: async (accountId?: string) => {
        await getKpis();
      },

      kpi: {
        addNewKPI: async () => {
          KPIStore.editorKPIs.push(getNewKPI());
        },
        saveEditorKPIs: async (isEditMode: boolean, kpis: KPI[]) => {
          try {
            let kpisFromServer: KPI[] = [];
            if (!isEditMode) {
              for (let index = 0; index < kpis.length; index++) {
                const kpiItem = kpis[index];
                const response = await addKpi({
                  variables: {
                    status: 'active',
                    group: kpiItem.group,
                    name: kpiItem.name,
                    type: kpiItem.type,
                    description: kpiItem.description,
                  },
                });
                // @ts-ignore
                kpisFromServer.push(response.data.kpiIndicatorAdd);
                // @ts-ignore
              }
              const kpiAddResponse: IBaseResponse<KPI[]> = {
                errorCode: 0,
                payload: kpisFromServer,
              };

              return kpiAddResponse;
            } else {
              const kpiItem = kpis[0];
              const response = await updateKpi({
                variables: {
                  id: editKpiId || '',
                  // @ts-ignore
                  accountId: ACCOUNT_ID,
                  status: 'active',
                  group: kpiItem.group,
                  name: kpiItem.name,
                  type: kpiItem.type,
                  description: kpiItem.description,
                },
              });
              // @ts-ignore
              kpisFromServer.push(response.data.kpiIndicatorUpdate);

              const kpiUpdateResponse: IBaseResponse<KPI[]> = {
                errorCode: 0,
                payload: kpisFromServer,
              };

              return kpiUpdateResponse;
            }
          } catch (err: any) {
            const kpiAddResponse: IBaseResponse<KPI[]> = {
              errorCode: -1,
              errorMessage: err,
            };
            return kpiAddResponse;
          }
        },
        deleteKPI: async (id: string) => {
          await deleteKpi({ variables: { id: id } });
          const kpiDeleteResponse: IBaseResponse<KPI[]> = {
            errorCode: 0,
          };

          return kpiDeleteResponse;
        },
      },
      editor: {
        editorKPIChange: (editorKPI: any) => {
          KPIStore.editorKPIs = KPIStore.editorKPIs.map((eKPI) => {
            const matched = editorKPI.id === eKPI.id;
            return matched ? { ...eKPI, ...editorKPI } : eKPI;
          });
        },
        editorKPIDelete: (editorKPI: any) => {
          KPIStore.editorKPIs = KPIStore.editorKPIs.filter(
            (eKPI) => eKPI.id !== editorKPI.id,
          );
        },
      },
    },
    models: {
      search: {
        value: searchValue,
        groupId,
      },
      openKPIDrawer,
      editorKPIs: KPIStore.editorKPIs,
      kpis: toJS(KPIStore.kpis).filter((k) =>
        isKPIIncluded(k, {
          searchValue,
          groupId,
        }),
      ),
      loading,
      groups: KPIStore.groupedKPIs.map(({ name }) => ({
        id: name,
        name,
      })),
      types: types.map((name) => ({
        id: name,
        name,
      })),
      groupedKPIs: KPIStore.groupedKPIs,
      kpisByIds: KPIStore.kpisByIds,
    },
  };
}
